import { uuidv7 } from "uuidv7";
import * as Y from "yjs";

import { FogRoom } from "@/features/battlemap/types";
import { RoomTooltip } from "@/shared/components/Tooltip";
import { useBattlemapStore } from "@/stores/useBattlemapStore";

const EDIT_BUTTON_CLASSES =
  "flex-1 px-3 py-1.5 text-xs bg-sky-600 hover:bg-sky-700 text-white rounded transition-colors";
const CANCEL_BTN_CLASSES =
  "flex-1 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors";
const ICON_BTN_CLASSES =
  "p-1.5 text-slate-400 hover:text-white transition-colors rounded hover:bg-slate-700";

interface RoomPanelProps {
  roomsArray: Y.Array<FogRoom>;
  doc: Y.Doc;
}

export function RoomPanel({ roomsArray, doc }: RoomPanelProps) {
  const {
    rooms,
    toggleRoom,
    renameRoom,
    updateRoomDescription,
    deleteRoom,
    createRoom,
    editingRoomId,
    editingRoomField,
    editingRoomValue,
    setEditingRoom,
  } = useBattlemapStore();

  const handleStartEdit = (room: FogRoom, field: "name" | "description") => {
    const initialValue = field === "name" ? room.name : room.description || "";
    setEditingRoom(room.id, field, initialValue);
  };

  const handleSaveEdit = () => {
    if (!editingRoomId || !editingRoomField || !editingRoomValue.trim()) {
      return;
    }

    if (editingRoomField === "name") {
      renameRoom(editingRoomId, editingRoomValue.trim(), roomsArray, doc);
    } else {
      updateRoomDescription(
        editingRoomId,
        editingRoomValue.trim(),
        roomsArray,
        doc
      );
    }
    setEditingRoom(null, null, "");
  };

  const handleCancelEdit = () => {
    setEditingRoom(null, null, "");
  };

  const handleEditInputChange = (value: string) => {
    setEditingRoom(editingRoomId, editingRoomField, value);
  };

  const handleCreateRoom = () => {
    const id = uuidv7();
    const newRoom: FogRoom = {
      id,
      name: `Room ${rooms.length + 1}`,
      color: "#3b82f6",
      shapeIds: [],
      doorIds: [],
      bounds: [],
      visible: true,
      isRevealed: false,
    };
    createRoom(newRoom, roomsArray, doc);
  };

  const getRoomClasses = (isRevealed: boolean) => {
    return isRevealed
      ? "bg-sky-900/30 border border-sky-700/50"
      : "bg-slate-900/50 border border-slate-700";
  };

  const getToggleButtonClasses = (isRevealed: boolean) => {
    return isRevealed
      ? "bg-sky-600 hover:bg-sky-700 text-white"
      : "bg-slate-700 hover:bg-slate-600 text-slate-300";
  };

  const renderIconButton = (
    onClick: () => void,
    title: string,
    className: string,
    children: React.ReactNode
  ) => (
    <button onClick={onClick} className={className} title={title}>
      {children}
    </button>
  );

  const renderEditIcon = (d: string) => (
    <svg
      className="w-3.5 h-3.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={d}
      />
    </svg>
  );

  return (
    <div className="h-full flex flex-col bg-slate-800 border-l border-slate-700">
      <div className="flex items-center justify-between p-3 border-b border-slate-700 flex-shrink-0">
        <h3 className="text-sm font-semibold text-slate-200">Rooms</h3>
        <button
          onClick={handleCreateRoom}
          className="text-xs bg-sky-600 hover:bg-sky-500 text-white px-2 py-1 rounded transition-colors"
        >
          + New
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {rooms.length === 0 ? (
          <div className="p-4 text-center text-slate-500 text-xs">
            No rooms detected yet.
            <br />
            Use fog fill tool to create rooms.
          </div>
        ) : (
          <div className="space-y-2">
            {rooms.map((room) => (
              <div
                key={room.id}
                className={`p-3 rounded transition-colors touch-manipulation ${getRoomClasses(
                  room.isRevealed
                )}`}
              >
                {editingRoomId === room.id && editingRoomField === "name" ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editingRoomValue}
                      onChange={(e) => handleEditInputChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveEdit();
                        if (e.key === "Escape") handleCancelEdit();
                      }}
                      className="w-full px-3 py-2 text-sm bg-slate-900 border border-slate-600 rounded text-slate-200 focus:outline-none focus:border-sky-500"
                      autoFocus
                      placeholder="Room name"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        className={EDIT_BUTTON_CLASSES}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className={CANCEL_BTN_CLASSES}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : editingRoomId === room.id &&
                  editingRoomField === "description" ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingRoomValue}
                      onChange={(e) => handleEditInputChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveEdit();
                        }
                        if (e.key === "Escape") handleCancelEdit();
                      }}
                      className="w-full px-3 py-2 text-sm bg-slate-900 border border-slate-600 rounded text-slate-200 focus:outline-none focus:border-sky-500 resize-none"
                      rows={3}
                      placeholder="Room description..."
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        className={EDIT_BUTTON_CLASSES}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className={CANCEL_BTN_CLASSES}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <RoomTooltip
                        roomName={room.name}
                        roomDescription={room.description}
                      >
                        <span className="text-sm text-slate-200 font-medium truncate cursor-help">
                          {room.name}
                        </span>
                      </RoomTooltip>
                      <div className="flex gap-1">
                        {renderIconButton(
                          () => handleStartEdit(room, "name"),
                          "Rename",
                          ICON_BTN_CLASSES,
                          renderEditIcon(
                            "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          )
                        )}
                        {renderIconButton(
                          () => handleStartEdit(room, "description"),
                          "Add description",
                          ICON_BTN_CLASSES,
                          renderEditIcon("M4 6h16M4 12h16M4 18h7")
                        )}
                        {renderIconButton(
                          () => deleteRoom(room.id, roomsArray, doc),
                          "Delete",
                          ICON_BTN_CLASSES,
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        )}
                      </div>
                    </div>

                    {room.description && (
                      <p className="text-xs text-slate-400 line-clamp-2">
                        {room.description}
                      </p>
                    )}

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleRoom(room.id, roomsArray, doc)}
                        className={`flex-1 px-3 py-1.5 text-xs rounded transition-colors touch-manipulation ${getToggleButtonClasses(
                          room.isRevealed
                        )}`}
                      >
                        {room.isRevealed ? "Hide Room" : "Reveal Room"}
                      </button>
                      <div className="px-2 py-1 text-xs text-slate-500 bg-slate-800/50 rounded">
                        {room.doorIds.length} door
                        {room.doorIds.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
